import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import MentorVideoHero from '../components/MentorVideoHero';
import HybridBookingDrawer from '../components/HybridBookingDrawer';
import styles from './Mentorship.module.css';

const Mentorship = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  
  const [benefitsRef, benefitsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [stepsRef, stepsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const benefits = [
    {
      icon: '🎯',
      title: 'Portfolio Clarity',
      description: 'Get honest feedback on what\'s working and what\'s not in your portfolio.'
    },
    {
      icon: '🏛️',
      title: 'Gallery Approach Strategy',
      description: 'Learn proven tactics for approaching galleries and building relationships.'
    },
    {
      icon: '🚀',
      title: 'Practical Career Moves',
      description: 'Discover your next strategic steps to advance your art career.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Pick a time',
      description: 'Choose from available 45-minute slots that work with your schedule.'
    },
    {
      number: '02', 
      title: 'Meet on Google Meet',
      description: 'Join Todd for an honest, no-holds-barred critique and career conversation.'
    },
    {
      number: '03',
      title: 'Receive PDF recap within 24h',
      description: 'Get a detailed summary of key insights and next steps via email.'
    }
  ];

  const testimonial = {
    quote: "Todd's feedback was brutally honest but exactly what I needed. His insights helped me refocus my portfolio and I landed my first gallery show within 6 months.",
    author: "Sarah Martinez",
    title: "Contemporary Artist"
  };

  const faqs = [
    {
      question: "Will this guarantee gallery representation?",
      answer: "No guarantees in the art world - Todd provides honest insights and strategic advice based on decades of experience. Success depends on your commitment to implementing the feedback and continuing to develop your practice."
    },
    {
      question: "Can I cancel or reschedule?",
      answer: "Sessions are non-refundable once booked. However, you can reschedule free of charge with at least 48 hours notice. Last-minute changes may result in forfeiture of your session."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className={styles.container}>
      {/* Hero Section with Video */}
      <MentorVideoHero />

      {/* About Your Mentor Section */}
      <section className={styles.mentorSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.mentorContent}>
            <h2 className={styles.mentorTitle}>About Your Mentor</h2>
            <div className={styles.mentorBio}>
              <p className={styles.mentorDescription}>
                <strong>Todd Williamson</strong> is a master artist with global gallery representation and decades of experience navigating the art world at the highest levels.
              </p>
              <div className={styles.credentialsList}>
                <div className={styles.credential}>
                  <span className={styles.credentialIcon}>🏛️</span>
                  <span>Official Venice Biennale Artist</span>
                </div>
                <div className={styles.credential}>
                  <span className={styles.credentialIcon}>🏆</span>
                  <span>Pollock Prize for Creativity Winner</span>
                </div>
                <div className={styles.credential}>
                  <span className={styles.credentialIcon}>🌍</span>
                  <span>Global Gallery Representation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <motion.section 
        ref={benefitsRef}
        className={styles.benefitsSection}
        initial={{ opacity: 0, y: 50 }}
        animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
      >
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>What You'll Get</h2>
          <div className={styles.benefitsGrid}>
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className={styles.benefitCard}
                initial={{ opacity: 0, y: 30 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className={styles.benefitIcon}>{benefit.icon}</div>
                <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                <p className={styles.benefitDescription}>{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* How It Works Section */}
      <motion.section 
        ref={stepsRef}
        className={styles.stepsSection}
        initial={{ opacity: 0, y: 50 }}
        animate={stepsInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
      >
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>How It Works</h2>
          <div className={styles.stepsContainer}>
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className={styles.stepCard}
                initial={{ opacity: 0, x: -30 }}
                animate={stepsInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className={styles.stepNumber}>{step.number}</div>
                <div className={styles.stepContent}>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDescription}>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Social Proof Section */}
      <section className={styles.testimonialSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.testimonialCard}>
            <div className={styles.testimonialQuote}>"{testimonial.quote}"</div>
            <div className={styles.testimonialAuthor}>
              <div className={styles.authorName}>{testimonial.author}</div>
              <div className={styles.authorTitle}>{testimonial.title}</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
          <div className={styles.faqContainer}>
            {faqs.map((faq, index) => (
              <div key={index} className={styles.faqItem}>
                <button
                  className={styles.faqQuestion}
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  aria-expanded={openFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  {faq.question}
                  <span className={styles.faqIcon}>
                    {openFaq === index ? '−' : '+'}
                  </span>
                </button>
                <div 
                  id={`faq-answer-${index}`}
                  className={`${styles.faqAnswer} ${openFaq === index ? styles.faqAnswerOpen : ''}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                >
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky Booking CTA */}
      <div className={styles.stickyBooking}>
        <button
          className={styles.bookingCta}
          onClick={() => setIsBookingOpen(true)}
          aria-label="Book your mentorship session with Todd Williamson for $300"
        >
          Book Your Session – $300
        </button>
      </div>

      {/* Booking Drawer */}
      <HybridBookingDrawer 
        isOpen={isBookingOpen} 
        onClose={() => setIsBookingOpen(false)}
      />
    </div>
  );
};

export default Mentorship; 